import React from 'react';
import { Image, View } from 'react-native';
import { Carousel } from 'react-native-reanimated-carousel';
import { DisplayScreen } from '@oolio-group/domain';
import { isVideoUri } from './isVideoUri';
import { useMediaItems } from './useMediaItems';
import { styles } from './Media.styles';

type MediaItem = { type: 'image' | 'video'; uri: string };

interface Props {
  config?: DisplayScreen;
  autoPlay?: boolean;
  intervalMs?: number;
}

const toItem = (uri: string): MediaItem => ({
  type: isVideoUri(uri) ? 'video' : 'image',
  uri,
});

const resolveItems = (config?: DisplayScreen): MediaItem[] => {
  const assets = config?.assets;
  if (assets && assets.length > 0) {
    return assets
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(asset => toItem(asset.uri));
  }
  if (config?.backgroundUri) {
    return [toItem(config.backgroundUri)];
  }
  return [];
};

// Web plays videos inline, so unlike native they count as renderable media.
export const hasRenderableMedia = (config?: DisplayScreen): boolean =>
  resolveItems(config).length > 0;

const ImageView: React.FC<{ uri: string }> = ({ uri }) => (
  <Image source={{ uri }} style={styles.fill} resizeMode="cover" />
);

const VideoView: React.FC<{ uri: string; isActive: boolean }> = ({
  uri,
  isActive,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive]);

  return (
    <video
      ref={videoRef}
      src={uri}
      muted
      loop
      playsInline
      style={{ flex: 1, width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

const renderItem = (item: MediaItem, isActive: boolean) =>
  item.type === 'video' ? (
    <VideoView uri={item.uri} isActive={isActive} />
  ) : (
    <ImageView uri={item.uri} />
  );

export const Media: React.FC<Props> = ({
  config,
  autoPlay = true,
  intervalMs = 5000,
}) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const items = useMediaItems(config, resolveItems, item => item.uri);
  const carouselRenderItem = React.useCallback(
    ({ item, index }: { item: MediaItem; index: number }) =>
      renderItem(item, index === activeIndex),
    [activeIndex],
  );

  if (items.length === 0) return null;

  if (items.length === 1) {
    return renderItem(items[0], true);
  }

  return (
    <View style={styles.fill}>
      <Carousel
        style={styles.fill}
        autoplay={autoPlay}
        autoplayInterval={intervalMs}
        loop
        animation={{ type: 'timing', duration: 1000 }}
        data={items}
        onSnapToItem={setActiveIndex}
        renderItem={carouselRenderItem}
      />
    </View>
  );
};
